import {Lock, Sparkles, TrendingUp} from 'lucide-react'
import type { PreviewData } from '@/lib/preview'

interface TierTeaserProps {
  preview: PreviewData
}

const tierMeta: Record<string, { color: string; emoji: string; pct: string }> = {
  emerging: { color: '#F59E0B', emoji: '🌱', pct: '0–34%' },
  developing: { color: '#0F766E', emoji: '🚀', pct: '35–59%' },
  established: { color: '#0F766E', emoji: '⚡', pct: '60–84%' },
  leading: { color: '#0A0A0A', emoji: '👑', pct: '85–100%' },
}

function TierTeaser({ preview }: TierTeaserProps) {
  const { tier, percent } = preview
  const meta = tierMeta[tier.level]
  const previewRecs = tier.recommendations.slice(0, 1)
  const lockedCount = tier.recommendations.length - previewRecs.length + tier.nextSteps.length

  return (
    <div className="mb-10 rounded-3xl overflow-hidden border border-[#E5E5E5] bg-white shadow-lg shadow-[#0A0A0A]/5">
      {/* Header band */}
      <div
        className="px-6 md:px-8 py-5 flex items-center justify-between"
        style={{ background: `linear-gradient(135deg, ${meta.color}10, ${meta.color}25)` }}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl" aria-hidden="true">{meta.emoji}</span>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">
              Your Preliminary Tier
            </div>
            <div className="font-display text-xl md:text-2xl font-semibold" style={{ color: meta.color }}>
              {tier.title}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl md:text-4xl font-semibold tabular-nums">
            {percent}<span className="text-base text-[#0A0A0A]/30">%</span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0A0A0A]/40">
            {meta.pct} range
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 md:px-8 py-6">
        <p className="text-sm md:text-base text-[#0A0A0A]/75 leading-relaxed mb-5">
          {tier.description}
        </p>

        <div className="space-y-3">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0F766E] flex items-center gap-2">
            <Sparkles size={12} />
            Sneak peek · 1 of {tier.recommendations.length} recommendations
          </div>
          {previewRecs.map((rec, i) => (
            <div key={i} className="flex gap-3 items-start p-4 rounded-xl bg-[#FAFAF7] border border-[#E5E5E5]">
              <span className="font-mono text-[11px] text-[#0F766E] mt-0.5 tabular-nums flex-shrink-0">01</span>
              <span className="text-sm text-[#0A0A0A]/80 leading-relaxed">{rec}</span>
            </div>
          ))}

          {/* Locked preview */}
          <div className="relative">
            <div className="flex gap-3 items-start p-4 rounded-xl bg-[#FAFAF7]/60 border border-dashed border-[#E5E5E5] blur-[2px] select-none" aria-hidden="true">
              <span className="font-mono text-[11px] text-[#0A0A0A]/30 mt-0.5 tabular-nums flex-shrink-0">02</span>
              <span className="text-sm text-[#0A0A0A]/40 leading-relaxed">
                {tier.recommendations[1] || 'Strategic next-step recommendations tailored to your readiness level.'}
              </span>
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0A0A0A] text-white rounded-full font-mono text-[10px] uppercase tracking-widest shadow-lg">
                <Lock size={11} />
                Unlock {lockedCount}+ more
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-[#E5E5E5] flex items-center gap-2 text-xs text-[#0A0A0A]/60">
          <TrendingUp size={14} className="text-[#F59E0B]" />
          <span>Complete the form below to unlock your full report, category breakdown, and execution roadmap.</span>
        </div>
      </div>
    </div>
  )
}

export default TierTeaser
